type CountryLike =
    | { slug?: string | null; name?: string | null }
    | null
    | undefined;

type CityLike =
    | { slug?: string | null; name?: string | null; nameStd?: string | null }
    | null
    | undefined;

/**
 * Collapses accidental multi-level percent-encoding (e.g. a raw route param
 * that was already encoded before it reached us) back to plain text, so a
 * leftover literal '%' never gets re-encoded into an ever-growing %25 chain
 * by the platform's own URL encoder.
 */
export function collapseEncoding(value: string): string {
    let current = value;
    let iterations = 0;
    while (iterations < 10 && current.includes('%')) {
        let decoded: string;
        try {
            decoded = decodeURIComponent(current);
        } catch {
            break;
        }
        if (decoded === current) break;
        current = decoded;
        iterations++;
    }
    return current;
}

export function cityNameToSlug(name: string): string {
    return collapseEncoding(name.trim()).replace(/ /g, '-').toLowerCase();
}

export function getCountrySlug(country: CountryLike, fallback = ''): string {
    const fromSlug = country?.slug?.trim().toLowerCase();
    if (fromSlug) return collapseEncoding(fromSlug);
    if (country?.name) return cityNameToSlug(country.name);
    return fallback.trim().toLowerCase();
}

/** English city from API slug (360630-Cairo → cairo). Never use translated name. */
export function getCitySlug(city: CityLike, fallback = ''): string {
    const raw = city?.slug?.trim();
    if (raw) {
        const english = raw.replace(/^\d+-/, '');
        return cityNameToSlug(english || raw);
    }
    return fallback.trim().toLowerCase();
}

/**
 * Prefer API slug; fall back to numeric id (same as ListingCard) so slug-less
 * workspaces/listings never build a truncated path that drops the last segment.
 */
export function getEntitySegment(
    slug?: string | null,
    id?: string | number | null
): string {
    const fromSlug = slug?.trim();
    if (fromSlug) return collapseEncoding(fromSlug);
    if (id != null && id !== '') return String(id);
    return '';
}

export interface WorkspacePathParts {
    country?: CountryLike;
    countrySlug?: string;
    city?: CityLike;
    citySlug?: string;
    workspaceSlug?: string | null;
    workspaceId?: string | number | null;
    listingSlug?: string | null;
    listingId?: string | number | null;
}

/** Build an internal workspace path using API slugs (no percent-encoding). */
export function buildWorkspacePath({
    country,
    countrySlug: countrySlugOverride,
    city,
    citySlug: citySlugOverride,
    workspaceSlug,
    workspaceId,
    listingSlug,
    listingId
}: WorkspacePathParts): string {
    const countrySegment =
        countrySlugOverride?.trim().toLowerCase() || getCountrySlug(country);
    const citySegment =
        citySlugOverride?.trim().toLowerCase() || getCitySlug(city);
    const workspaceSegment = getEntitySegment(workspaceSlug, workspaceId);
    const listingSegment = getEntitySegment(listingSlug, listingId);

    const segments = ['workspaces', countrySegment, citySegment].filter(
        Boolean
    );

    if (workspaceSegment) segments.push(workspaceSegment);
    if (listingSegment) segments.push(listingSegment);

    return segments.join('/');
}

function normalizePathSegment(segment: string): string {
    try {
        return decodeURIComponent(segment).toLowerCase();
    } catch {
        return segment.toLowerCase();
    }
}

export function normalizeWorkspacePath(path: string): string {
    return path
        .replace(/^\/+/, '')
        .split('/')
        .filter(Boolean)
        .map(normalizePathSegment)
        .join('/');
}

/**
 * Only redirect when we have a complete, safer canonical path.
 * Avoids redirecting to truncated paths (e.g. missing workspace segment when
 * slug is empty) which would send users to the city explore page.
 */
export function workspacePathNeedsRedirect(
    currentPath: string,
    canonicalPath: string,
    options?: { requireWorkspace?: boolean; requireListing?: boolean }
): boolean {
    const current = normalizeWorkspacePath(currentPath);
    const canonical = normalizeWorkspacePath(canonicalPath);

    if (!canonical || canonical === current) return false;

    const parts = canonical.split('/');
    // workspaces / country / city [/ workspace [/ listing ]]
    if (parts.length < 3 || parts[0] !== 'workspaces') return false;
    if (!parts[1] || !parts[2]) return false;

    if (options?.requireWorkspace && parts.length < 4) return false;
    if (options?.requireListing && parts.length < 5) return false;

    return true;
}

/** Absolute path for Next redirect(), optionally keeping the current query string. */
export function toWorkspaceRedirectHref(
    canonicalPath: string,
    searchParams?: Record<string, string | string[] | undefined> | null
): string {
    // Same casing as the canonical tag; encoded because redirect headers must be ASCII.
    const path = `/${canonicalPath
        .replace(/^\/+/, '')
        .split('/')
        .filter(Boolean)
        .map((segment) => encodeURIComponent(collapseEncoding(segment)))
        .join('/')}`;
    if (!searchParams) return path;

    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(searchParams)) {
        for (const item of Array.isArray(value) ? value : [value]) {
            if (item !== undefined) query.append(key, item);
        }
    }
    const queryString = query.toString();

    return queryString ? `${path}?${queryString}` : path;
}
